import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

const httpOptions = {
  headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
};

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private baseUrl = environment.serverUrl + 'customers';
  constructor(private http: HttpClient) {}

  private deviceAdminUrl = environment.serverUrl + 'admin/devices';

  /** Devices, pending approvals and what recovery is possible for one user. */
  getDeviceOverview(profileId: number | string): Observable<any> {
    return this.http.get(`${this.deviceAdminUrl}/${profileId}`);
  }

  /**
   * Removes the user's devices so a new one can sign in, and ends their
   * sessions. startFresh additionally discards the account's chat identity,
   * which makes older messages permanently unreadable.
   */
  resetDevices(
    profileId: number | string,
    body: { reason: string; startFresh?: boolean; confirmUsername?: string }
  ): Observable<any> {
    return this.http.post(`${this.deviceAdminUrl}/${profileId}/reset`, body, httpOptions);
  }

  login(login: any): Observable<Object> {
    return this.http.post(`${environment.serverUrl}login/admin`, {
      email: login.Email,
      password: login.Password,
    });
  }

  userList(
    page: number,
    size: number,
    search: string = '',
    startDate?: any,
    endDate?: any
  ): Observable<any> {
    const data = {
      page: page,
      size: size,
      search: search,
      startDate: startDate,
      endDate: endDate,
    };
    return this.http.post(`${this.baseUrl}`, data);
  }

  getUserDetailsById(Id: any): Observable<any> {
    return this.http.get(`${this.baseUrl}/${Id}`);
  }

  changeUserStatus(id: number, status: string): Observable<any> {
    const userId = id;
    const IsActive = status;
    return this.http.get(
      `${this.baseUrl}/change-status/${userId}?IsActive=${IsActive}`
    );
  }

  createAdmin(id: number): Observable<any> {
    const userId = id;
    return this.http.get(`${this.baseUrl}/create-admin/${userId}`);
  }

  setRtkAdmin(id: number, status: 'Y' | 'N'): Observable<any> {
    return this.http.get(`${this.baseUrl}/set-rtk-admin/${id}?status=${status}`);
  }

  changeAccountType(id: number, status: string): Observable<any> {
    const userId = id;
    const type = status;
    return this.http.get(
      `${this.baseUrl}/change-user-type/${userId}?type=${type}`
    );
  }

  suspendUser(id: number, status: string): Observable<any> {
    const userId = id;
    const IsSuspended = status;
    return this.http.get(
      `${this.baseUrl}/suspend-user/${userId}?IsSuspended=${IsSuspended}`
    );
  }

  activateMedia(id: number, status: number): Observable<any> {
    const userId = id;
    const IsSuspended = status;
    return this.http.get(
      `${this.baseUrl}/activate-media/${userId}?MediaApproved=${IsSuspended}`
    );
  }

  deleteUser(id: number): Observable<any> {
    return this.http.delete(`${this.baseUrl}/${id}`, { responseType: 'text' });
  }

  getUserList(): Observable<any> {
    return this.http.get(`${this.baseUrl}/get`);
  }

  getProfileList(searchText: string = ''): Observable<object> {
    return this.http.get(
      `${this.baseUrl}/search-user?searchText=${searchText}`
    );
  }

  getCountriesData(): Observable<{ country_code: string; country: string }[]> {
    return this.http.get<{ country_code: string; country: string }[]>(
      `${this.baseUrl}/countries`
    );
  }

  getZipData(zip: string, country: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/zip/${zip}?country=${country}`);
  }

  updateProfile(id: number, customer: any): Observable<Object> {
    return this.http.put(`${this.baseUrl}/profile/${id}`, customer);
  }

  getUserEmailList(data: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/get-emails`, data);
  }

  deleteAllData(id: number): Observable<any> {
    return this.http.delete(`${this.baseUrl}/delete-all/${id}`);
  }

  updatePassword(currentPassword: string, newPassword: string): Observable<any> {
    const data = {
      currentPassword: currentPassword,
      newPassword: newPassword
    };
    const url = `${this.baseUrl}/update-password`;
    console.log('Updating password at URL:', url);
    return this.http.put(url, data);
  }

  resendActivationEmail(id: number): Observable<any> {
    return this.http.get(`${this.baseUrl}/resend-activation-email/${id}`);
  }

  // Data for who applied for the license
  getAllClients(page?: number, size?: number, search?: string, startDate?: any, endDate?: any): Observable<any> {
    if (page && size) {
      // If pagination parameters provided, construct the query string
      let query = `?limit=${size}&offset=${(page - 1) * size}`;
      if (search) query += `&search=${search}`;
      if (startDate) query += `&startDate=${startDate}`;
      if (endDate) query += `&endDate=${endDate}`;
      return this.http.get(`${environment.serverUrl}/licenses${query}`);
    }
    return this.http.get(`${environment.serverUrl}/licenses`);
  }

  // Get all registered agents with pagination and search
  getAllAgents(limit: number = 10, offset: number = 0, search: string = ''): Observable<any> {
    let query = `?limit=${limit}&offset=${offset}`;
    if (search) {
      query += `&search=${encodeURIComponent(search)}`;
    }
    return this.http.get(`${environment.serverUrl}agents/registered/all${query}`);
  }

  // Get single agent by userId
  getAgentById(userId: number): Observable<any> {
    return this.http.get(`${environment.serverUrl}agents/registered/${userId}`);
  }
}
